import type {
  DataGridFilterState,
  DataGridRowSelectionState,
  DataGridSortingState,
  TableFilterState,
} from '@ibsys/kit';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { ConversationListItem } from '@/entities/conversations';
import { useHasPermission } from '@/entities/profile';
import { toDataGridFilters, toDataGridSorting, useSettings, useTablePresets, useTableState } from '@/entities/settings';
import { usePhrasePlaybackScope } from '@/features/phrase-playback';
import { useSmartSearchLazy } from '@/features/smart-search';
import { DEFAULT_DIALOG_SORTING } from './constants';
import { useDescriptionActions, useFiltersDescription } from './filtersDescription';
import { useColumnsPanelWidth } from './useColumnsPanelWidth';
import { useCommentChangeReload } from './useCommentChangeReload';
import { useContentSearchState } from './useContentSearchState';
import { useDialogColumns } from './useDialogColumns';
import { useDialogDownload } from './useDialogDownload';
import { useDialogRowClick } from './useDialogRowClick';
import { useDialogSearchHandlers } from './useDialogSearchHandlers';
import { useExternalSelection } from './useExternalSelection';
import { useFilterLayerActions } from './useFilterLayerActions';
import { useLazyPageFetch } from './useLazyPageFetch';
import { useListScrollPosition } from './useListScrollPosition';
import { useMassActions } from './useMassActions';

/**
 * Раскрытых групп колонок ещё нет в настройках — все свёрнуты.
 * Ссылка постоянная: панель управляется контролируемо, и новый пустой массив на каждый рендер
 * выглядел бы для неё сменой состояния.
 */
const NO_EXPANDED_GROUPS: string[] = [];

/** Пустой отбор раздела — постоянная ссылка, чтобы состав запроса не пересобирался каждый рендер */
const NO_SECTION_FILTERS: TableFilterState = [];

/** Параметры состояния списка диалогов в конкретном разделе */
export type UseDialogListStateParams = {
  /** Имя таблицы — ключ вида списка в настройках пользователя */
  tableName: string;
  /** Загруженная страница списка — нужна раскладкам, показывающим запись рядом со списком */
  onLoaded?: (data: ConversationListItem[]) => void;
  /**
   * Отбор самого раздела: выборка, которой ограничен список. В панели фильтров его нет —
   * пользователь его не задаёт, а сужает своими условиями поверх него.
   */
  sectionFilters?: TableFilterState;
  /**
   * Отметки строк ведёт сам раздел, а не список. Так набор записей выборки переживает смену
   * отбора: его набирают разными фильтрами и разными страницами, а сохраняют одним составом.
   */
  rowSelectionOverride?: {
    rowSelection: DataGridRowSelectionState;
    onRowSelectionChange: (next: DataGridRowSelectionState) => void;
  };
  /**
   * Отбор раздела — единственный: условия пользователя (фильтры, поиск, поиск по содержимому)
   * поверх него не действуют. Нужно там, где раздел показывает заданный им самим состав целиком
   * (показ набранного): иначе показанное сузилось бы отбором, из которого набирали.
   */
  isSectionFiltersExclusive?: boolean;
  /**
   * Массовые действия над диалогами в разделе есть. Раздел, который служит только отбору диалогов
   * (выборки), их не показывает: обрабатывают диалоги в разделе диалогов.
   */
  hasMassActions?: boolean;
};

/**
 * Состояние списка диалогов: вид таблицы, поиск, отбор, выгрузка и массовые действия.
 *
 * Собран отдельно от секции, потому что список диалогов показывают несколько разделов
 * («Диалоги», закреплённый пресет, «Выборки»): у них своё хранилище вида, свой отбор раздела
 * и свой блок в строке управления, а сам список — один и тот же.
 */
export const useDialogListState = ({
  tableName,
  onLoaded,
  sectionFilters = NO_SECTION_FILTERS,
  rowSelectionOverride,
  hasMassActions = true,
  isSectionFiltersExclusive = false,
}: UseDialogListStateParams) => {
  const { id } = useParams<{ id?: string }>();
  const { dialogsPage: dialogsSettings, setDialogsSettings } = useSettings();

  // Массовый запуск обработки диалогов доступен только под отдельным разрешением и только там,
  // где раздел вообще показывает массовые действия.
  const canRunPipeline = useHasPermission('ADMIN_CONVERSATION_PIPELINE') && hasMassActions;

  const { settings, actions } = useTableState(tableName, 'localStorage', { sorting: DEFAULT_DIALOG_SORTING });
  const {
    activePreset,
    isDirty: isPresetDirty,
    apply: applyPreset,
    enterView,
    exitSelection,
    saveCurrentToActivePreset,
  } = useTablePresets(tableName);

  const [reloadKey, setReloadKey] = useState(0);
  const triggerReload = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  useCommentChangeReload(triggerReload);

  // --- внешняя выборка диалогов (переход с авто-инсайта/зоны качества дашборда) ---
  const {
    filters: effectiveSettingsFilters,
    sorting: selectionSorting,
    activeSelectionKey,
    activeSelection,
    unsavedSelection,
    onDismiss: onSelectionHintDismiss,
  } = useExternalSelection({
    tableName,
    filters: settings.filters ?? [],
    sorting: settings.sorting ?? DEFAULT_DIALOG_SORTING,
    dismissedSelectionKey: settings.dismissedSelectionKey,
    onDismissedSelectionKeyChange: actions.setDismissedSelectionKey,
    selectionDescription: settings.selectionDescription,
    onSelectionDescriptionChange: actions.setSelectionDescription,
  });

  // Настройки с наложенным слоем выборки: на свежем переходе фильтры/сортировка уже отражают выборку
  // и сортировку по умолчанию — их и передаём в состояние поиска по содержимому.
  const effectiveSettings = useMemo(
    () => ({ ...settings, filters: effectiveSettingsFilters, sorting: selectionSorting }),
    [settings, effectiveSettingsFilters, selectionSorting],
  );

  // --- состояние поиска по содержимому диалога ---
  const contentSearch = useContentSearchState({ settings: effectiveSettings, actions, onReload: triggerReload });
  const {
    activeMode: activeContentMode,
    committedValue: committedContentValue,
    hasSearch: hasContentSearch,
    columnsMode: contentColumnsMode,
    reset: resetContentSearch,
    contentSearchOptions,
    effectiveSorting: effectiveSettingsSorting,
    handleSortingChange: handleContentSearchSortingChange,
    filterUserColumnOrder,
    getEffectiveColumnOrder,
  } = contentSearch;

  // Отбор запроса: условия раздела и условия пользователя вместе. В панель фильтров и в описание
  // отбора уходят только пользовательские — поля раздела там нет, и снять его фильтром нельзя.
  // При исключительном отборе раздела условия пользователя в запрос не идут вовсе.
  const requestFilters = useMemo(() => {
    if (isSectionFiltersExclusive) return sectionFilters;
    return sectionFilters.length ? [...effectiveSettingsFilters, ...sectionFilters] : effectiveSettingsFilters;
  }, [effectiveSettingsFilters, sectionFilters, isSectionFiltersExclusive]);

  const sorting = useMemo<DataGridSortingState>(
    () => toDataGridSorting(effectiveSettingsSorting),
    [effectiveSettingsSorting],
  );
  const filters = useMemo<DataGridFilterState>(
    () => toDataGridFilters(effectiveSettingsFilters),
    [effectiveSettingsFilters],
  );

  // --- колонки таблицы и параметры запроса из их видимости ---
  const {
    columns,
    isLoading,
    isReady,
    gridColumnProps,
    columnOrder,
    columnPinning,
    visibleAttributePaths,
    visibleMainColumns,
    expertReportSearchParams,
    searchDialogsPage,
  } = useDialogColumns({
    settings,
    actions,
    filters,
    sorting,
    contentSearch: { mode: contentColumnsMode, hasQuery: hasContentSearch },
    filterUserColumnOrder,
    getEffectiveColumnOrder,
    mainColumns: dialogsSettings.mainColumns,
    onMainColumnsChange: (mainColumns) => setDialogsSettings({ mainColumns }),
  });

  const {
    value,
    onChange,
    searchMode,
    setSearchMode,
    predicateInfo,
    searchError,
    fetchData,
    buildRequestParams,
    clearSearch,
    isSearchLoading,
  } = useSmartSearchLazy({
    entityType: 'conversation',
    initialSearch: settings.search,
    dialogsPage: searchDialogsPage,
    // Поиск по содержимому — такое же условие пользователя, как фильтры: при исключительном
    // отборе раздела он в запрос не идёт, но остаётся набранным в панели поиска.
    contentSearchOptions: isSectionFiltersExclusive ? undefined : contentSearchOptions,
    isQueryIgnored: isSectionFiltersExclusive,
  });

  const { totalCount, cacheVersion, gridKey, fetchPage } = useLazyPageFetch({
    fetchData,
    filters: requestFilters,
    sorting: effectiveSettingsSorting,
    onLoaded,
    cacheKey: {
      gridSorting: sorting,
      gridFilters: filters,
      sectionFilters,
      visibleMainColumns,
      visibleAttributePaths,
      expertReportSearchParams,
      activeContentMode,
      committedContentValue,
      reloadKey,
    },
  });

  const { listCompositionKey, initialScrollIndex, onScrollIndexChange } = useListScrollPosition({
    filters: requestFilters,
    sorting: effectiveSettingsSorting,
    search: settings.search,
    activeContentMode,
    committedContentValue,
    savedScrollKey: settings.scrollKey,
    savedScrollIndex: settings.scrollIndex,
    onScrollIndexChange: actions.setScrollIndex,
  });

  // Прослушивание найденной реплики живёт ровно в том списке, из которого запущено:
  // смена состава, переход в диалог и уход со списка его прекращают.
  usePhrasePlaybackScope(`${listCompositionKey}|${id ?? ''}`);

  const {
    isMassActionsActive,
    rowSelection,
    selectedCount,
    hasPipelineSelection,
    isPreparingSelection,
    isRunPipelinePending,
    onToggleMassActions,
    onRowSelectionChange,
    onRunPipeline,
  } = useMassActions({
    canRunPipeline,
    selectionOverride: rowSelectionOverride,
    fetchData,
    filters: requestFilters,
    sorting: effectiveSettingsSorting,
    listCompositionKey,
  });

  const { onDownloadList, isDownloadPending } = useDialogDownload({
    buildRequestParams,
    filters: requestFilters,
    sorting: effectiveSettingsSorting,
    visibleAttributePaths,
    visibleMainColumns,
    expertReportSearchParams,
    columnOrder,
    columnPinning,
  });

  const { onSortingChange, onFiltersChange, onSearch, onClearSearch, onModeChange } = useDialogSearchHandlers({
    searchMode,
    searchValue: value,
    setSearchMode,
    clearSearch,
    onContentSearchSortingChange: handleContentSearchSortingChange,
    applyFilters: actions.onFiltersChange,
    commitSearch: actions.setSearch,
    triggerReload,
  });

  const { canReset, canSaveActivePreset, canDeselectSelection, onResetFilters, onDeselectSelection } =
    useFilterLayerActions({
      activePreset,
      isPresetDirty,
      applyPreset,
      enterView,
      exitSelection,
      filters: effectiveSettingsFilters,
      sorting: settings.sorting,
      activeSelectionKey,
      onFiltersChange: actions.onFiltersChange,
      onClear: onClearSearch,
      onResetContentSearch: resetContentSearch,
    });

  const onClickRow = useDialogRowClick();

  const filtersDescription = useFiltersDescription({
    columns,
    columnOrder,
    filters: effectiveSettingsFilters,
    sorting: effectiveSettingsSorting,
    selection: activeSelection,
  });

  // Описание не только читается, но и управляет отбором: пишет то же состояние таблицы,
  // что и контролы в шапке колонки.
  const filtersDescriptionActions = useDescriptionActions({
    sorting: effectiveSettingsSorting,
    onSortingChange: handleContentSearchSortingChange,
    filters: effectiveSettingsFilters,
    onFiltersChange: actions.onFiltersChange,
    canRemoveSelection: canDeselectSelection,
    onRemoveSelection: onDeselectSelection,
  });

  // Ширина панели управления колонками: локальное состояние на время перетаскивания ручки,
  // запись в настройки таблицы — с задержкой.
  const { width: columnsPanelWidth, onWidthChange: onColumnsPanelWidthChange } = useColumnsPanelWidth(
    settings.columnsPanelWidth,
    actions.setColumnsPanelWidth,
  );

  return {
    /** Готовые пропсы презентера списка — раздел дополняет их своим блоком строки управления */
    gridProps: {
      columns,
      sorting,
      onSortingChange,
      filters,
      onFiltersChange,
      totalCount,
      fetchPage,
      cacheVersion,
      gridKey,
      searchValue: value,
      onSearchChange: onChange,
      onSearch,
      searchMode,
      onModeChange,
      onClearSearch,
      predicateInfo,
      searchError,
      isSearchLoading,
      isLoading: isLoading || !isReady,
      activeRowId: id,
      onClickRow,
      initialScrollIndex,
      onScrollIndexChange,
      gridColumnProps,
      columnsPanelWidth,
      onColumnsPanelWidthChange,
      columnsPanelExpandedGroups: settings.columnsPanelExpandedGroups ?? NO_EXPANDED_GROUPS,
      onColumnsPanelExpandedGroupsChange: actions.setColumnsPanelExpandedGroups,
      activeSidePanel: settings.activeSidePanel ?? null,
      onActiveSidePanelChange: actions.setActiveSidePanel,
      isSidePanelPinned: Boolean(settings.sidePanelPinned),
      onSidePanelPinnedChange: actions.setSidePanelPinned,
      headerOrientation: settings.headerOrientation,
      onHeaderOrientationChange: actions.setHeaderOrientation,
      filtersDescription,
      filtersDescriptionActions,
      activeContentTab: contentSearch.activeTab,
      contentSearchInputValue: contentSearch.inputValue,
      onContentSearchInputChange: contentSearch.setInputValue,
      onContentSearchClear: contentSearch.clearInput,
      onContentSearchTabChange: contentSearch.handleTabChange,
      onContentSearchSubmit: contentSearch.handleSubmit,
      phraseSearchRole: contentSearch.phraseSearchRole,
      onPhraseSearchRoleChange: contentSearch.handlePhraseSearchRoleChange,
      phraseSearchEmployeeIds: contentSearch.phraseSearchEmployeeIds,
      onPhraseSearchEmployeeIdsChange: contentSearch.handlePhraseSearchEmployeeIdsChange,
      commentAuthorIds: contentSearch.commentAuthorIds,
      onCommentAuthorIdsChange: contentSearch.handleCommentAuthorIdsChange,
      canReset,
      onResetFilters,
      onDownloadList,
      isDownloadPending,
      canOpenActions: canRunPipeline,
      isMassActionsActive,
      isRowSelectionActive: isMassActionsActive,
      onToggleMassActions,
      rowSelection,
      onRowSelectionChange,
      selectedCount,
      hasPipelineSelection,
      onRunPipeline,
      isRunPipelinePending: isRunPipelinePending || isPreparingSelection,
      isPreparingSelection,
    },
    /** Слой отбора раздела «Диалоги» — пресеты и выборка перехода с дашборда */
    presets: {
      activePreset,
      canSaveActivePreset,
      onSaveActivePreset: saveCurrentToActivePreset,
      canDeselectSelection,
      onDeselectSelection,
      unsavedSelection,
      onSelectionHintDismiss,
    },
    /** Срез списка и состояние таблицы — разделу они нужны для собственного слоя отбора */
    list: {
      settings,
      actions,
      buildRequestParams,
      /** Перезапрос показанных строк: разделу он нужен после правки того, чем список ограничен */
      reload: triggerReload,
      /** Отбор, заданный пользователем: без условий самого раздела */
      userFilters: effectiveSettingsFilters,
      requestFilters,
      sorting: effectiveSettingsSorting,
    },
  };
};
